/**
 * Purchase Request List Component
 * Display and manage all purchase requests
 */

import {
    ArrowPathIcon,
    Bars3Icon,
    CheckCircleIcon,
    DocumentIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    PencilIcon,
    PlusIcon,
    PrinterIcon,
    Squares2X2Icon,
    TrashIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { inventoryAPI } from '../../../services/inventoryApi';
import adminAPI from '../../../services/adminApi';
import ConfirmationModal from '../../Common/ConfirmationModal';
import CustomSelect from '../../Common/CustomSelect';
import LoadingSpinner from '../../Common/LoadingSpinner';
import PermissionGuard from '../../Common/PermissionGuard';
import TextInput from '../../Common/TextInput';
import PurchaseRequestForm from './PurchaseRequestForm';
import PurchaseRequestView from './PurchaseRequestView';
import { openPurchaseRequestReport } from '../../../utils/openInventoryReport';
import { canPrintPurchaseRequest } from '../../../utils/documentPrintGate';

const PurchaseRequestList = () => {
    const { hasPermission } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [purchaseRequests, setPurchaseRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [view, setView] = useState('card');
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmComment, setConfirmComment] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingPR, setEditingPR] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingPR, setViewingPR] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        limit: 20,
        total: 0
    });
    const [companyInfo, setCompanyInfo] = useState({});

    // Load view preference from localStorage
    useEffect(() => {
        const savedView = localStorage.getItem('purchaseRequestListView');
        if (savedView) {
            setView(savedView);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
                if (!locationId) return;
                const res = await adminAPI.getLocationById(locationId);
                const loc = res.data?.data || res.data || {};
                const addr = loc.address || {};
                setCompanyInfo({
                    name: loc.name || loc.locationName || '',
                    street: addr.street || addr.addressLine1 || '',
                    city: addr.city || '',
                    zipCode: addr.zipCode || addr.postalCode || '',
                });
            } catch (e) {
                console.warn('[PurchaseRequestList] could not load location info:', e.message);
            }
        })();
    }, []);

    const handlePrintPurchaseRequest = async (pr) => {
        await openPurchaseRequestReport(pr, companyInfo);
    };

    useEffect(() => {
        fetchPurchaseRequests();
    }, [pagination.page, pagination.limit, statusFilter, priorityFilter, searchInput]);

    const fetchPurchaseRequests = async () => {
        try {
            setLoading(true);

            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search: searchInput || undefined,
                status: statusFilter || undefined,
                priority: priorityFilter || undefined,
            };

            const response = await inventoryAPI.getPurchaseRequests({ ...params, sortBy: 'createdAt', sortOrder: 'desc' });
            setPurchaseRequests(response.data || []);
            setPagination(response.pagination || pagination);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to fetch purchase requests');
            console.error('Fetch purchase requests error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchInput(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleViewChange = (newView) => {
        setView(newView);
        try {
            localStorage.setItem('purchaseRequestListView', newView);
        } catch (error) {
            console.warn('Failed to save view preference:', error);
        }
    };

    const handleResetFilters = () => {
        setSearchInput('');
        setStatusFilter('');
        setPriorityFilter('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const hasFilters = () => searchInput || statusFilter || priorityFilter;

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    };

    const handleAddPurchaseRequest = () => {
        setEditingPR(null);
        setShowModal(true);
    };

    // Once approved (or converted to a PO) a purchase request is locked
    const canEditPR = (pr) => ['draft', 'submitted', 'pending_approval'].includes(pr.status);
    const canDeletePR = (pr) => !['approved', 'converted_to_po'].includes(pr.status);

    const handleEditPurchaseRequest = (pr) => {
        if (!canEditPR(pr)) {
            toast.error('Purchase request cannot be edited in its current status');
            return;
        }
        setEditingPR(pr);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingPR(null);
    };

    const handleModalSuccess = (data) => {
        setShowModal(false);
        setEditingPR(null);
        fetchPurchaseRequests(); // Refresh the list
        toast.success(`Purchase request ${editingPR ? 'updated' : 'created'} successfully`);
    };

    // Back from the stand-alone form (/new, /edit/:id): open the request that was just saved
    useEffect(() => {
        const openId = location.state?.openPRId;
        if (!openId) return;
        setViewingPR({ _id: openId });
        setShowViewModal(true);
        navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: {} });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const handleViewPurchaseRequest = (pr) => {
        setViewingPR(pr);
        setShowViewModal(true);
    };

    const handleDeletePurchaseRequest = (pr) => {
        if (['approved', 'converted_to_po'].includes(pr.status)) {
            toast.error('Cannot delete approved or converted purchase requests');
            return;
        }

        setConfirmAction({
            title: 'Delete Purchase Request',
            message: `Are you sure you want to delete purchase request ${pr.prNumber}? This action cannot be undone.`,
            confirmText: 'Delete',
            type: 'danger',
            onConfirm: async () => {
                try {
                    await inventoryAPI.deletePurchaseRequest(pr._id);
                    toast.success('Purchase request deleted successfully');
                    fetchPurchaseRequests();
                } catch (error) {
                    toast.error(error.response?.data?.message || 'Failed to delete purchase request');
                } finally {
                    setConfirmAction(null);
                }
            }
        });
    };

    const canApprovePR = (pr) =>
        hasPermission('INVENTORY:PURCHASE_REQUESTS:APPROVE') &&
        ['submitted', 'pending_approval'].includes(pr.status);

    const handleApprovePurchaseRequest = (pr) => {
        setConfirmComment('');
        setConfirmAction({
            title: 'Approve Purchase Request',
            message: `Approve purchase request ${pr.prNumber}?`,
            confirmText: 'Approve',
            type: 'success',
            showCommentBox: true,
            commentPlaceholder: 'Add approval comments (optional)...',
            onConfirm: async (comment) => {
                try {
                    await inventoryAPI.approvePurchaseRequest(pr._id, { comments: comment || '' });
                    toast.success('Purchase request approved successfully');
                    fetchPurchaseRequests();
                } catch (error) {
                    toast.error(error.response?.data?.message || 'Failed to approve purchase request');
                } finally {
                    setConfirmAction(null);
                    setConfirmComment('');
                }
            }
        });
    };

    const handleRejectPurchaseRequest = (pr) => {
        setConfirmComment('');
        setConfirmAction({
            title: 'Reject Purchase Request',
            message: `Provide a reason for rejecting purchase request ${pr.prNumber}.`,
            confirmText: 'Reject',
            type: 'danger',
            showCommentBox: true,
            requireComment: true,
            commentPlaceholder: 'Rejection reason (required)...',
            onConfirm: async (comment) => {
                try {
                    await inventoryAPI.rejectPurchaseRequest(pr._id, { reason: comment, comments: '' });
                    toast.success('Purchase request rejected');
                    fetchPurchaseRequests();
                } catch (error) {
                    toast.error(error.response?.data?.message || 'Failed to reject purchase request');
                } finally {
                    setConfirmAction(null);
                    setConfirmComment('');
                }
            }
        });
    };

    const getStatusBadgeColor = (status) => {
        const colors = {
            draft: 'bg-gray-100 text-gray-800 border-gray-200',
            submitted: 'bg-blue-100 text-blue-800 border-blue-200',
            pending_approval: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            approved: 'bg-green-100 text-green-800 border-green-200',
            rejected: 'bg-red-100 text-red-800 border-red-200',
            converted_to_po: 'bg-purple-100 text-purple-800 border-purple-200',
            cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
        };
        return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const getPriorityBadgeColor = (priority) => {
        const colors = {
            normal: 'bg-green-100 text-green-800 border-green-200',
            urgent: 'bg-orange-100 text-orange-800 border-orange-200',
            critical: 'bg-red-100 text-red-800 border-red-200'
        };
        return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'LKR'
        }).format(amount);
    };

    const filteredPurchaseRequests = purchaseRequests || [];

    const renderPurchaseRequestCard = (pr) => (
        <div key={pr._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-32 w-full">
            <div className="p-3 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-semibold flex-shrink-0 ${getPriorityBadgeColor(pr.priority)}`}>
                            {pr.priority?.charAt(0)?.toUpperCase() || 'N'}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center space-x-1">
                                {pr.prNumber}
                            </div>
                        </div>
                    </div>

                    <div className="ml-2 flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                handleViewPurchaseRequest(pr);
                            }}
                            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                            title="View purchase request"
                        >
                            <EyeIcon className="w-3 h-3" />
                        </button>
                        {canApprovePR(pr) && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleApprovePurchaseRequest(pr);
                                    }}
                                    className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                                    title="Approve purchase request"
                                >
                                    <CheckCircleIcon className="w-3 h-3" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleRejectPurchaseRequest(pr);
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                    title="Reject purchase request"
                                >
                                    <XCircleIcon className="w-3 h-3" />
                                </button>
                            </>
                        )}
                        {canPrintPurchaseRequest(pr.status) && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePrintPurchaseRequest(pr);
                                }}
                                className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                title="Print PDF"
                            >
                                <PrinterIcon className="w-3 h-3" />
                            </button>
                        )}
                        {canEditPR(pr) && <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:UPDATE">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleEditPurchaseRequest(pr);
                                }}
                                className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                                title="Edit purchase request"
                            >
                                <PencilIcon className="w-3 h-3" />
                            </button>
                        </PermissionGuard>}
                        {canDeletePR(pr) && <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:DELETE">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleDeletePurchaseRequest(pr);
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                title="Delete purchase request"
                            >
                                <TrashIcon className="w-3 h-3" />
                            </button>
                        </PermissionGuard>}
                    </div>
                </div>

                {/* Status and Details */}
                <div className="flex items-center justify-between mb-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusBadgeColor(pr.status)}`}>
                        {pr.status?.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-gray-500">
                        {formatDate(pr.requestDate)}
                    </span>
                </div>

                {/* Bottom Section */}
                <div className="flex items-end justify-between mt-auto">
                    <div className="text-xs text-gray-600">
                        <div className="flex items-center">
                            <DocumentIcon className="w-3 h-3 mr-1" />
                            {pr.lineItems?.length || 0} items
                        </div>
                    </div>
                    <div className="text-xs text-right">
                        {pr.totalEstimatedAmount ? (
                            <span className="font-medium text-gray-900">
                                {formatCurrency(pr.totalEstimatedAmount)}
                            </span>
                        ) : (
                            <span className="text-gray-500">No estimate</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPurchaseRequestRow = (pr) => (
        <tr key={pr._id} className="hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${getPriorityBadgeColor(pr.priority)}`}></div>
                    {pr.prNumber}
                </div>
            </td>

            <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPriorityBadgeColor(pr.priority)}`}>
                    {pr.priority}
                </span>
            </td>

            <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(pr.status)}`}>
                    {pr.status?.replace(/_/g, ' ')}
                </span>
            </td>

            <td className="px-4 py-3 text-sm text-gray-900">
                <div>{pr.lineItems?.length || 0} items</div>
                {pr.totalEstimatedAmount && (
                    <div className="text-xs text-gray-500">{formatCurrency(pr.totalEstimatedAmount)}</div>
                )}
            </td>

            <td className="px-4 py-3 text-right text-sm font-medium">
                <div className="flex justify-end space-x-1">
                    <button
                        onClick={() => handleViewPurchaseRequest(pr)}
                        className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                        title="View purchase request"
                    >
                        <EyeIcon className="w-4 h-4" />
                    </button>
                    {canApprovePR(pr) && (
                        <>
                            <button
                                onClick={() => handleApprovePurchaseRequest(pr)}
                                className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-all"
                                title="Approve purchase request"
                            >
                                <CheckCircleIcon className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handleRejectPurchaseRequest(pr)}
                                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                                title="Reject purchase request"
                            >
                                <XCircleIcon className="w-4 h-4" />
                            </button>
                        </>
                    )}
                    {canPrintPurchaseRequest(pr.status) && (
                        <button
                            onClick={() => handlePrintPurchaseRequest(pr)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                            title="Print PDF"
                        >
                            <PrinterIcon className="w-4 h-4" />
                        </button>
                    )}
                    {canEditPR(pr) && <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:UPDATE">
                        <button
                            onClick={() => handleEditPurchaseRequest(pr)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                            title="Edit purchase request"
                        >
                            <PencilIcon className="w-4 h-4" />
                        </button>
                    </PermissionGuard>}
                    {canDeletePR(pr) && <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:DELETE">
                        <button
                            onClick={() => handleDeletePurchaseRequest(pr)}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                            title="Delete purchase request"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </PermissionGuard>}
                </div>
            </td>
        </tr>
    );

    if (loading && filteredPurchaseRequests.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="bg-white shadow-sm border border-gray-200">
            {/* Error Display */}
            {error && (
                <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
                    <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mr-2" />
                        <p className="text-sm font-medium text-red-800">{error}</p>
                        <button
                            onClick={() => setError('')}
                            className="ml-auto text-red-600 hover:text-red-500"
                        >
                            <XCircleIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                    {/* Search */}
                    <div className="flex-1">
                        <TextInput
                            variant="input"
                            type="text"
                            placeholder="Search by PR number, description..."
                            value={searchInput}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="w-full"
                        />
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-2 lg:flex-nowrap">
                        <CustomSelect
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="min-w-[120px]"
                            size="sm"
                            variant="minimal"
                            options={[
                                { value: '', label: 'All Status' },
                                { value: 'submitted', label: 'Submitted' },
                                { value: 'pending_approval', label: 'Pending Approval' },
                                { value: 'approved', label: 'Approved' },
                                { value: 'rejected', label: 'Rejected' },
                                { value: 'converted_to_po', label: 'Converted to PO' },
                                { value: 'cancelled', label: 'Cancelled' },
                            ]}
                        >
                        </CustomSelect>

                        <CustomSelect
                            value={priorityFilter}
                            onChange={(e) => setPriorityFilter(e.target.value)}
                            className="min-w-[100px]"
                            size="sm"
                            variant="minimal"
                            options={[
                                { value: '', label: 'All Priority' },
                                { value: 'normal', label: 'Normal' },
                                { value: 'urgent', label: 'Urgent' },
                                { value: 'critical', label: 'Critical' },
                            ]}
                        >
                        </CustomSelect>

                        {hasFilters() && (
                            <button
                                onClick={handleResetFilters}
                                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded transition-all"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                     <div className="flex items-center gap-2">
                        {/* View Toggle */}
                        <div className="bg-white rounded-md border border-gray-200 p-1 flex">
                            <button
                                onClick={() => handleViewChange('card')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                            >
                                <Squares2X2Icon className="w-3 h-3 mr-1 inline" />
                                Cards
                            </button>
                            <button
                                onClick={() => handleViewChange('list')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                            >
                                <Bars3Icon className="w-3 h-3 mr-1 inline" />
                                List
                            </button>
                        </div>

                        {/* Add Purchase Request Button */}
                        <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:CREATE">
                            <button
                                onClick={handleAddPurchaseRequest}
                                className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                            >
                                <PlusIcon className="w-3.5 h-3.5 mr-1.5" />
                                New Request
                            </button>
                        </PermissionGuard>
                    </div>
                </div>
            </div>


            {/* Pagination Section */}
            <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Results Info */}
                    <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-600">
                            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                            {pagination.total} results
                            {loading && (
                                <div className="inline-block ml-2">
                                    <ArrowPathIcon className="w-3 h-3 animate-spin opacity-75" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center gap-3">
                        {/* Items per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 font-medium">Show:</span>
                            <CustomSelect
                                value={pagination.limit}
                                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                                disabled={loading}
                                options={[
                                    { value: 10, label: '10' },
                                    { value: 20, label: '20' },
                                    { value: 50, label: '50' },
                                    { value: 100, label: '100' }
                                ]}
                                size="sm"
                                variant="minimal"
                                className="min-w-[70px]"
                            />
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.page <= 1 || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="First page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                            </button>

                            <button
                                onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                                disabled={pagination.page <= 1 || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span className="hidden sm:inline">Previous</span>
                            </button>

                            {/* Page Numbers */}
                            <div className="hidden md:flex items-center gap-1">
                                {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                                    const pageNum = i + 1;
                                    const isCurrentPage = pageNum === pagination.page;

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            disabled={loading}
                                            className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${isCurrentPage
                                                ? 'bg-indigo-600 text-white shadow-sm'
                                                : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                {pagination.pages > 7 && (
                                    <span className="px-2 text-gray-400 text-sm">...</span>
                                )}
                            </div>

                            <button
                                onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                            >
                                <span className="hidden sm:inline">Next</span>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            <button
                                onClick={() => handlePageChange(pagination.pages)}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="Last page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            {view === 'card' ? (
                <div className="px-4 sm:px-6 lg:px-8 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {filteredPurchaseRequests.length === 0 ? (
                            <div className="col-span-full bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                                <DocumentIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                <h3 className="text-sm font-medium text-gray-900 mb-2">No purchase requests found</h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    {hasFilters() ? 'Try adjusting your search or filters' : 'Get started by creating your first purchase request'}
                                </p>
                                {!hasFilters() && (
                                    <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:CREATE">
                                        <button
                                            onClick={handleAddPurchaseRequest}
                                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                                        >
                                            <PlusIcon className="w-4 h-4 mr-2" />
                                            New Request
                                        </button>
                                    </PermissionGuard>
                                )}
                            </div>
                        ) : (
                            filteredPurchaseRequests.map(renderPurchaseRequestCard)
                        )}
                    </div>
                </div>
            ) : (
                <div className="px-4 sm:px-6 lg:px-8 py-4">
                    <div className="overflow-x-auto rounded-sm border border-gray-200">
                        <table className="min-w-max w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">PR Number</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Priority</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Details</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredPurchaseRequests.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-4 py-12 text-center">
                                            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                            <h3 className="text-sm font-medium text-gray-900 mb-2">No purchase requests found</h3>
                                            <p className="text-sm text-gray-500 mb-4">
                                                {hasFilters() ? 'Try adjusting your search or filters' : 'Get started by creating your first purchase request'}
                                            </p>
                                            {!hasFilters() && (
                                                <PermissionGuard permission="INVENTORY:PURCHASE_REQUESTS:CREATE">
                                                    <button
                                                        onClick={handleAddPurchaseRequest}
                                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                                                    >
                                                        <PlusIcon className="w-4 h-4 mr-2" />
                                                        New Request
                                                    </button>
                                                </PermissionGuard>
                                            )}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPurchaseRequests.map(renderPurchaseRequestRow)
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmAction && (
                <ConfirmationModal
                    isOpen={!!confirmAction}
                    onClose={() => {
                        setConfirmAction(null);
                        setConfirmComment('');
                    }}
                    onConfirm={confirmAction.onConfirm}
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmText={confirmAction.confirmText}
                    type={confirmAction.type}
                    showCommentBox={confirmAction.showCommentBox}
                    comment={confirmComment}
                    onCommentChange={setConfirmComment}
                    commentPlaceholder={confirmAction.commentPlaceholder}
                    disableConfirm={confirmAction.requireComment && !confirmComment.trim()}
                />
            )}

            {/* Purchase Request Form Modal */}
            {showModal && (
                <PurchaseRequestForm
                    open={showModal}
                    onClose={handleModalClose}
                    onSuccess={handleModalSuccess}
                    isModal={true}
                    initialData={editingPR}
                />
            )}

            {/* Purchase Request View Modal */}
            {showViewModal && viewingPR && (
                <PurchaseRequestView
                    isOpen={showViewModal}
                    purchaseRequestId={viewingPR._id}
                    onClose={() => {
                        setShowViewModal(false);
                        setViewingPR(null);
                    }}
                    onEdit={(pr) => {
                        setShowViewModal(false);
                        setViewingPR(null);
                        handleEditPurchaseRequest(pr);
                    }}
                    onRefresh={fetchPurchaseRequests}
                />
            )}
        </div>
    );
};

export default PurchaseRequestList;